import { useCallback, useEffect, useMemo, useState } from 'react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import Stack from '@mui/material/Stack';
import Skeleton from '@mui/material/Skeleton';
import { LoadingButton } from '@/components/shared/LoadingButton';
import { FilterActions } from './FilterActions/FilterActions';
import { AssemblyFilters } from './AssemblyFilters/AssemblyFilters';
import { DynamicFilters } from './DynamicFilters/DynamicFilters';
import * as state from '@/components/Analysis/state';

import { primary } from '@/theme/colors';
import { useSnackBarMessage } from '@/utils/useSnackBarMessage';
import { useNavbarHeight } from '@/hooks/useNavbarHeight';
import {
  TemplateFieldTypeEnum,
  TemplateTypeEnum,
  useAllAssemblyTemplatesForFilterSideBarQuery,
} from '@/__generated__/graphql';
import { useGetStructureId } from '@/hooks/useGetStructureId';
import { useHavePermission } from '@/hooks';
import { DateFiltersByField, Permissions } from '@/types';
import type { DateFilterValue } from '@/components/Analysis/state';
import { DateFilter } from './DateFilter';

const FiltersSkeleton = () => {
  return (
    <>
      {Array.from({ length: 8 }, (_, index) => (
        <Skeleton
          key={index}
          variant="rectangular"
          width="100%"
          height={20}
          sx={{ marginTop: '32px' }}
        />
      ))}
    </>
  );
};

type FilterSideBarProps = {
  onClear?: () => void;
  showDynamicFilters?: boolean;
  showOnlyWorkpackFilter?: boolean; // New prop: show only workpack in DynamicFilters
};

export const FilterSideBar = ({
  onClear,
  showDynamicFilters = true,
  showOnlyWorkpackFilter = false,
}: FilterSideBarProps) => {
  const assemblyTemplate = useAtomValue(state.assemblyTemplate);
  const setAllAssemblyTemplates = useSetAtom(state.allAssemblyTemplates);
  const [applyFilters, setApplyFilters] = useAtom(state.applyFilters);
  const { showSnackBar } = useSnackBarMessage({ variant: 'filled' });
  const [queryLoading, setQueryLoading] = useState<boolean>(applyFilters);
  const navbarHeight = useNavbarHeight();
  const structureId = useGetStructureId();
  const canReadDefects = useHavePermission(Permissions.READ_DEFECTS);

  // filters to apply
  const selectedFilteredDeckIds = useAtomValue(state.selectedFilteredDeckIds);
  const selectedFilteredPaintRegionIds = useAtomValue(state.selectedFilteredPaintRegionIds);
  const selectedFilteredWorkpackIds = useAtomValue(state.selectedFilteredWorkpackIds);
  const selectedFilteredPoiTypes = useAtomValue(state.selectedFilteredPoiTypes);
  const selectedTemplateFilters = useAtomValue(state.selectedTemplateFiltersState);
  const dateFiltersDraft = useAtomValue(state.dateFilters);
  const appliedDateFiltersValue = useAtomValue(state.appliedDateFilters);
  const setAppliedDateFilters = useSetAtom(state.appliedDateFilters);
  const setFilters = useSetAtom(state.filters);

  const dateQueryFields = useMemo(() => {
    if (!assemblyTemplate?.fields) return [];
    return assemblyTemplate.fields.filter(
      (field) => field.type === TemplateFieldTypeEnum.Date && field.displayInQuery && field.key
    );
  }, [assemblyTemplate]);

  const selectedTemplateFiltersWithAppliedDates = useMemo(() => {
    const dateFiltersAsTemplateFilters = Object.entries(appliedDateFiltersValue).flatMap(
      ([fieldKey, value]) => {
        if (value.type === 'single' && value.date) {
          return [{ id: fieldKey, value: [], startDate: value.date, endDate: value.date }];
        }
        if (value.type === 'range' && value.startDate && value.endDate) {
          return [
            {
              id: fieldKey,
              value: [],
              startDate: value.startDate,
              endDate: value.endDate,
            },
          ];
        }
        return [];
      }
    );
    return [...selectedTemplateFilters, ...dateFiltersAsTemplateFilters];
  }, [appliedDateFiltersValue, selectedTemplateFilters]);

  const { data: templatesData } = useAllAssemblyTemplatesForFilterSideBarQuery({
    variables: {
      structureId,
      type: TemplateTypeEnum.Assembly,
    },
    skip: !structureId,
    fetchPolicy: 'cache-first',
  });

  useEffect(() => {
    const assemblyTemplates = templatesData?.allTemplates;
    if (assemblyTemplates) {
      setAllAssemblyTemplates(templatesData.allTemplates);
      let selectedTemplate = assemblyTemplates[0];
      if (!selectedTemplate) return;
      if (!canReadDefects) {
        selectedTemplate = {
          ...selectedTemplate,
          fields:
            selectedTemplate?.fields?.filter(
              (templateField) =>
                !templateField.permission || templateField.permission !== Permissions.READ_DEFECTS
            ) || [],
        };
      }
    }
  }, [templatesData?.allTemplates, setAllAssemblyTemplates, canReadDefects]);

  const filtersLoaded = useMemo(() => {
    return !!assemblyTemplate?.fields;
  }, [assemblyTemplate]);

  const handleClick = useCallback(() => {
    setAppliedDateFilters(
      JSON.parse(JSON.stringify(dateFiltersDraft)) as Record<string, DateFilterValue>
    );

    setApplyFilters(true);
    setQueryLoading(true);
    showSnackBar('Query has been applied successfully', 'success');
  }, [dateFiltersDraft, setAppliedDateFilters, setApplyFilters, showSnackBar]);

  useEffect(() => {
    if (!applyFilters) {
      setQueryLoading(false);
    }
  }, [applyFilters]);

  useEffect(() => {
    if (applyFilters) {
      if (
        selectedFilteredDeckIds.length > 0 ||
        selectedFilteredPaintRegionIds.length > 0 ||
        selectedFilteredWorkpackIds.length > 0 ||
        selectedFilteredPoiTypes.length > 0 ||
        selectedTemplateFilters.length > 0
      ) {
        const dateFilter: DateFiltersByField = Object.entries(appliedDateFiltersValue).reduce(
          (acc, [fieldKey, value]) => {
            if (value.type === 'range' && value.startDate && value.endDate) {
              acc[fieldKey] = {
                type: 'range',
                startDate: value.startDate,
                endDate: value.endDate,
              };
            } else if (value.type === 'single' && value.date) {
              acc[fieldKey] = { type: 'single', date: value.date };
            }
            return acc;
          },
          {} as DateFiltersByField
        );
        setFilters({
          deckIds: selectedFilteredDeckIds,
          paintRegionIds: selectedFilteredPaintRegionIds,
          templateFilters: selectedTemplateFiltersWithAppliedDates,
          workpackIds: selectedFilteredWorkpackIds,
          poiTypes: selectedFilteredPoiTypes,
          dateFilter: Object.keys(dateFilter).length ? dateFilter : undefined,
        });
      } else {
        setFilters(undefined);
      }
    }
  }, [
    setFilters,
    applyFilters,
    selectedFilteredDeckIds,
    selectedFilteredPaintRegionIds,
    selectedTemplateFilters,
    selectedFilteredWorkpackIds,
    selectedFilteredPoiTypes,
    selectedTemplateFiltersWithAppliedDates,
    appliedDateFiltersValue,
  ]);

  return (
    <Stack data-testid="filter-sidebar" sx={{ height: `calc(100vh - ${navbarHeight + 52}px)` }}>
      <Stack>
        <FilterActions onClear={onClear} />
      </Stack>
      {!filtersLoaded && <FiltersSkeleton />}
      {filtersLoaded && (
        <Stack
          sx={{
            overflowY: 'auto',

            msOverflowStyle: 'none',
            scrollbarWidth: 'none',
            '&::-webkit-scrollbar': { display: 'none' },
          }}
        >
          {showDynamicFilters && <DynamicFilters showOnlyWorkpack={showOnlyWorkpackFilter} />}
          {dateQueryFields.map((field) => (
            <DateFilter
              key={field.key || field.id}
              fieldKey={field.key || field.id}
              fieldName={field.name || 'Date'}
            />
          ))}
          <AssemblyFilters />
        </Stack>
      )}
      {filtersLoaded && (
        <Stack>
          <LoadingButton
            loading={queryLoading}
            disabled={queryLoading}
            onClick={handleClick}
            buttonVariant="contained"
            buttonStyles={{
              background: primary.darkBlue,
              height: '42px',
              borderRadius: 0.5,
              fontSize: '1.5rem',
              fontWeight: 500,
              mt: 2,
              mb: 2,
              width: '100%',
              p: 1,
              textTransform: 'none',
              '@media (max-width: 1728px)': {
                height: '32px',
                mt: 1,
                mb: 1,
              },
            }}
            buttonText="Apply Query"
          />
        </Stack>
      )}
    </Stack>
  );
};
